import React from 'react';
import { useSite } from '../useSite';
import { Seo } from '../components/Seo';
import { Container, Overline, SectionHeading } from '../components/Primitives';
import { CtaBand, FounderCards, PageHero, ProcessSteps } from '../components/Sections';
import { IntakeForm } from '../components/IntakeForm';
import { BullMark } from '../components/Brand';
import { Photo } from '../components/Photo';

const QR_SRC = '/images/vcard-qr.png';

export function HowWeWorkPage() {
  const { t } = useSite();
  const h = t.howWeWork;
  return (
    <>
      <Seo title={t.seo.howWeWork.title} description={t.seo.howWeWork.description} />
      <PageHero overline={h.overline} title={h.h1} intro={h.intro} photo="office" />
      <section className="bg-white py-20 md:py-24">
        <Container>
          <h2 className="mb-12 font-serif text-3xl text-kombu-green md:text-4xl">{h.processTitle}</h2>
          <ProcessSteps />
        </Container>
      </section>
      <section className="bg-bone-light py-20 md:py-24" data-testid="engagement-models">
        <Container>
          <SectionHeading title={h.modelsTitle} intro={t.brand.secondary} />
          <div className="mt-12 grid gap-px border border-tan/50 bg-tan/50 md:grid-cols-2">
            {h.models.map((m, i) => (
              <article key={m.name} className="flex flex-col bg-white p-8 md:p-10" data-testid={`engagement-model-${i}`}>
                <div className="flex items-start justify-between gap-4">
                  <h3 className="font-serif text-[1.7rem] leading-tight text-kombu-green">{m.name}</h3>
                  {m.badge && <span className="whitespace-nowrap border border-moss-green/40 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-moss-green">{m.badge}</span>}
                </div>
                <p className="mt-5 border-t border-tan/50 pt-4 text-xs uppercase tracking-[0.14em] text-cafe-noir/60">{m.priceNote}</p>
                <p className="mt-5 text-[15px] leading-relaxed text-cafe-noir/80">{m.text}</p>
              </article>
            ))}
          </div>
          <p className="mt-8 max-w-3xl text-sm leading-relaxed text-cafe-noir/70" data-testid="engagement-note">{h.note}</p>
        </Container>
      </section>
      <CtaBand />
    </>
  );
}

export function AboutPage() {
  const { t } = useSite();
  const a = t.about;
  return (
    <>
      <Seo title={t.seo.about.title} description={t.seo.about.description} />
      <PageHero overline={a.overline} title={a.h1} />
      <section className="bg-white py-20 md:py-24">
        <Container className="grid gap-14 lg:grid-cols-12">
          <div className="space-y-6 text-base leading-relaxed text-cafe-noir/85 md:text-lg lg:col-span-7">
            {a.paragraphs.map((x) => <p key={x}>{x}</p>)}
          </div>
          <div className="flex flex-col items-start gap-6 border-l border-tan pl-8 lg:col-span-5">
            <div className="aspect-[4/5] w-full max-w-sm overflow-hidden"><Photo name="office" sizes="(min-width: 1024px) 30vw, 100vw" /></div>
            <div className="flex items-center gap-4"><BullMark className="h-12" /><p className="font-serif text-2xl italic text-moss-green">{t.brand.tagline}</p></div>
          </div>
        </Container>
      </section>
      <section className="bg-bone-light py-20 md:py-24">
        <Container>
          <h2 className="font-serif text-3xl text-kombu-green md:text-4xl">{a.principlesTitle}</h2>
          <div className="mt-10 grid gap-10 md:grid-cols-3">
            {a.principles.map((x) => (
              <div key={x.title} className="border-t border-tan pt-5">
                <h3 className="font-serif text-2xl text-kombu-green">{x.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-cafe-noir/75">{x.text}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>
      <section className="bg-white py-20 md:py-24" id="leadership">
        <Container>
          <h2 className="mb-12 font-serif text-3xl text-kombu-green md:text-4xl">{a.leadershipTitle}</h2>
          <FounderCards />
          <p className="mt-10 max-w-3xl text-sm leading-relaxed text-cafe-noir/70" data-testid="about-independence">{a.independence}</p>
        </Container>
      </section>
      <CtaBand />
    </>
  );
}

export function InsightsPage() {
  const { t } = useSite();
  const i = t.insights;
  return (
    <>
      <Seo title={t.seo.insights.title} description={t.seo.insights.description} noindex />
      <PageHero overline={i.overline} title={i.h1} intro={i.intro} />
      <section className="bg-white py-20 md:py-24">
        <Container className="max-w-3xl">
          <h2 className="font-serif text-2xl text-kombu-green md:text-3xl">{i.topicsTitle}</h2>
          <ul className="mt-6 border-t border-tan/50" data-testid="insights-topics">
            {i.topics.map((x) => <li key={x} className="border-b border-tan/50 py-4 font-serif text-xl text-cafe-noir/90">{x}</li>)}
          </ul>
        </Container>
      </section>
      <CtaBand />
    </>
  );
}

export function BookPage() {
  const { t } = useSite();
  const b = t.book;
  return (
    <>
      <Seo title={t.seo.book.title} description={t.seo.book.description} />
      <PageHero overline={b.overline} title={b.h1} intro={b.intro}>
        <ol className="mt-10 flex flex-col gap-4 sm:flex-row sm:gap-10">
          {b.steps.map((s, i) => (
            <li key={s} className="flex items-center gap-3 text-sm text-cafe-noir/80"><span className="font-serif text-2xl text-moss-green">{i + 1}</span>{s}</li>
          ))}
        </ol>
      </PageHero>
      <section className="bg-white py-16 md:py-24" id="contacto">
        <Container className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-8"><IntakeForm /></div>
          <aside className="space-y-8 lg:col-span-4">
            <div className="border border-tan/60 bg-bone-light p-7">
              <Overline>{b.contactTitle}</Overline>
              {t.founders.map((f) => (
                <div key={f.email} className="mt-5">
                  <p className="font-serif text-xl text-kombu-green">{f.name}</p>
                  <a href={`mailto:${f.email}`} className="break-all text-sm text-cafe-noir/80 hover:text-kombu-green">{f.email}</a>
                </div>
              ))}
            </div>
            <div className="border border-tan/60 p-7 text-center" data-testid="vcard-qr">
              <p className="font-serif text-xl text-kombu-green">{b.qrTitle}</p>
              <p className="mt-2 text-xs text-cafe-noir/70">{b.qrText}</p>
              <img src={QR_SRC} alt="QR vCard Bullish Accounting" width="176" height="176" loading="lazy" className="mx-auto mt-5 h-44 w-44" />
            </div>
          </aside>
        </Container>
      </section>
    </>
  );
}

export function LegalPage({ doc }) {
  const { t } = useSite();
  const d = t.legal[doc];
  return (
    <>
      <Seo title={t.seo[doc].title} description={t.seo[doc].description} />
      <section className="bg-bone-light py-16 md:py-24">
        <Container className="max-w-3xl">
          <h1 className="font-serif text-4xl text-kombu-green md:text-5xl">{d.h1}</h1>
          <div className="mt-10 space-y-8">
            {d.sections.map((s) => (
              <section key={s.h}>
                <h2 className="font-serif text-2xl text-kombu-green">{s.h}</h2>
                {[].concat(s.p).map((x) => (
                  <p key={x} className="mt-2 text-[15px] leading-relaxed text-cafe-noir/85">{x}</p>
                ))}
              </section>
            ))}
          </div>
          <p className="mt-12 text-xs text-cafe-noir/60">{t.brand.legal}</p>
        </Container>
      </section>
    </>
  );
}
